import React, { FC, useEffect, useMemo, useState } from 'react';
import { Field, FieldProps, Form, Formik } from 'formik';
import * as Yup from 'yup';
import cx from 'classnames';

import DialogStyled from 'components/Organization/Placeholders/PlaceholderDialog/DialogStyled';
import { LocalizedStringsMap, SUPPORTED_LANGUAGES } from 'core/constants';
import ButtonCross from 'shared/components/Buttons/ButtonCross';
import ButtonDefault from 'shared/components/Buttons/ButtonsDefault';
import InputText from 'shared/components/FormInputs/InputText';
import InputErrorHint from 'shared/components/InputErrorHint';

import styles from './LiveScreenTextModal.module.scss';

export type TextSectionKey = 'topText' | 'bottomText';

export interface LiveScreenTextsValue {
	topText: LocalizedStringsMap;
	bottomText: LocalizedStringsMap;
}

interface LiveScreenTextModalProps {
	isOpen: boolean;
	initialSection: TextSectionKey;
	topText: LocalizedStringsMap;
	bottomText: LocalizedStringsMap;
	onClose: () => void;
	// eslint-disable-next-line no-unused-vars
	onApply: (values: LiveScreenTextsValue) => void;
}

const validationSchema = Yup.object().shape({
	topText: Yup.object().shape({
		en: Yup.string().trim().required('English top text is required'),
		fr: Yup.string(),
		he: Yup.string(),
		ru: Yup.string(),
		uk: Yup.string(),
	}),
	bottomText: Yup.object().shape({
		en: Yup.string().trim().required('English bottom text is required'),
		fr: Yup.string(),
		he: Yup.string(),
		ru: Yup.string(),
		uk: Yup.string(),
	}),
});

const countConfiguredLanguages = (map: LocalizedStringsMap): number =>
	SUPPORTED_LANGUAGES.filter(lang => Boolean((map[lang.key] || '').trim())).length;

export const LiveScreenTextModal: FC<LiveScreenTextModalProps> = ({
	isOpen,
	initialSection,
	topText,
	bottomText,
	onClose,
	onApply,
}) => {
	const [activeSection, setActiveSection] = useState<TextSectionKey>(initialSection);

	useEffect(() => {
		if (isOpen) {
			setActiveSection(initialSection);
		}
	}, [isOpen, initialSection]);

	const initialValues = useMemo<LiveScreenTextsValue>(
		() => ({
			topText: { ...topText },
			bottomText: { ...bottomText },
		}),
		[topText, bottomText]
	);

	const isTop = activeSection === 'topText';

	return (
		<DialogStyled open={isOpen} maxWidth="md">
			<section className={styles.dialog}>
				<div className={styles.dialog__header}>
					<ButtonCross clickHandler={onClose} parentClassName={styles.dialog__header_button} />
				</div>

				<Formik<LiveScreenTextsValue>
					enableReinitialize
					initialValues={initialValues}
					validationSchema={validationSchema}
					validateOnMount
					onSubmit={values => {
						onApply(values);
						onClose();
					}}
				>
					{({ values, isValid, setFieldValue, setFieldTouched }) => (
						<Form className={styles.dialog__form}>
							<div className={styles.dialog__top}>
								<h3 className={styles.dialog__title}>
									{isTop ? 'Edit Top Line Text' : 'Edit Bottom Line Text'}
								</h3>

								<div className={styles.dialog__tabs}>
									<button
										type="button"
										className={cx(styles.dialog__tab, {
											[styles.dialog__tab_active]: activeSection === 'topText',
										})}
										onClick={() => setActiveSection('topText')}
									>
										Top Line Text ({countConfiguredLanguages(values.topText)}/
										{SUPPORTED_LANGUAGES.length})
									</button>
									<button
										type="button"
										className={cx(styles.dialog__tab, {
											[styles.dialog__tab_active]: activeSection === 'bottomText',
										})}
										onClick={() => setActiveSection('bottomText')}
									>
										Bottom Line Text ({countConfiguredLanguages(values.bottomText)}/
										{SUPPORTED_LANGUAGES.length})
									</button>
								</div>
							</div>

							<div className={styles.dialog__fields}>
								{SUPPORTED_LANGUAGES.map(lang => {
									const fieldName = `${activeSection}.${lang.key}`;
									const inputId = `modal-${activeSection}-${lang.key}`;
									const lineLabel = isTop ? 'top' : 'bottom';

									return (
										<Field key={fieldName} name={fieldName}>
											{({ field, meta }: FieldProps<string, LiveScreenTextsValue>) => (
												<div className={styles.dialog__item}>
													<label className={styles.dialog__label} htmlFor={inputId}>
														{lang.label}
														{lang.required && (
															<span className={styles.dialog__required}>*</span>
														)}
													</label>
													<InputText
														id={inputId}
														field={field}
														dir={lang.dir}
														placeholder={
															lang.required
																? `Enter ${lineLabel} text in English`
																: `Enter ${lineLabel} text in ${lang.label} (optional)`
														}
														setFieldValue={setFieldValue}
														onBlur={() => setFieldTouched(fieldName, true)}
													/>
													{meta.error && meta.touched && (
														<InputErrorHint errorText={meta.error} disableMargin />
													)}
												</div>
											)}
										</Field>
									);
								})}
							</div>

							<div className={styles.dialog__footer}>
								<ButtonDefault
									title="Cancel"
									classList={['secondary', 'lg']}
									parentClassName={styles.dialog__footer_btn}
									clickHandler={onClose}
								/>
								<ButtonDefault
									submitType
									title="Apply"
									classList={['primary', 'lg']}
									parentClassName={styles.dialog__footer_btn}
									disabled={!isValid}
								/>
							</div>
						</Form>
					)}
				</Formik>
			</section>
		</DialogStyled>
	);
};
